// Upload → report. The column screen only appears when we can't read the file, or when the
// operator asks to change how we read it; extra details are optional, from the report.
// The rules run in the import worker, which returns the finished report.
import { useEffect, useRef, useState } from 'preact/hooks';
import { trackEvent } from '../../analytics/stats';
import { NO_DETAILS, type Details } from '../../import/details';
import { ImportSession } from '../../import/importSession';
import type { ImportSettings } from '../../import/normalise';
import type { NormaliseSummary } from '../../import/summary';
import type { CanonicalRota, Nation } from '../../model/types';
import type { Report } from '../../report/buildReport';
import { SAMPLES, sampleFile, type Sample } from '../../samples';
import { ReportView } from '../report/ReportView';
import type { TableInfo } from '../../worker/protocol';
import { DetailsStep } from './DetailsStep';
import { MappingStep } from './MappingStep';

const WORKER_FAILED =
  'Something went wrong while reading your rota, so we stopped. Please try again, or try a smaller export.';

interface Mapping {
  name: 'mapping';
  file: File;
  table: TableInfo;
  headers: string[];
  settings: ImportSettings;
  summary: NormaliseSummary;
  updating: boolean;
  /** Only present for verified vendor layouts (T06). */
  vendorName?: string;
}

interface ReportStep {
  name: 'report';
  report: Report;
  mapping: Mapping;
  details: Details;
}

type Step =
  | { name: 'upload'; error?: string }
  | { name: 'reading' }
  | Mapping
  | { name: 'details'; rota: CanonicalRota; back: ReportStep }
  | { name: 'checking' }
  | ReportStep;

export function ImportFlow() {
  const [step, setStep] = useState<Step>({ name: 'upload' });
  const [dragging, setDragging] = useState(false);
  // Asked once, at upload, and applied to every site (kept for "Check another rota").
  const [nation, setNation] = useState<Nation>('england');
  const outOfScope = nation === 'northern-ireland';
  const session = useRef<ImportSession | null>(null);
  const latestRequest = useRef(0);
  // Counted once, when a newly chosen file or sample first reaches the report. Only where it
  // came from is sent, never anything from the rota (src/analytics/stats.ts).
  const uncounted = useRef<'file' | 'sample' | null>(null);

  const closeSession = () => {
    session.current?.close();
    session.current = null;
  };
  useEffect(() => closeSession, []);

  const restart = (error?: string) => {
    closeSession();
    uncounted.current = null;
    setStep(error ? { name: 'upload', error } : { name: 'upload' });
  };

  async function open(file: File, sheetName?: string) {
    closeSession();
    setStep({ name: 'reading' });
    const result = await ImportSession.open(file, sheetName !== undefined ? { sheetName } : {});
    if (!result.ok) return restart(result.message);
    session.current = result.session;
    const { table, headers, settings, summary, vendorName } = result;
    const mapping: Mapping = {
      name: 'mapping',
      file,
      table,
      headers,
      settings,
      summary,
      updating: false,
      ...(vendorName !== undefined && { vendorName }),
    };
    // If we could read the columns, go straight to the report.
    if (summary.canContinue) return runCheck(mapping, NO_DETAILS);
    setStep(mapping);
  }

  function openFile(file: File) {
    uncounted.current = 'file';
    void open(file);
  }

  async function openSample(sample: Sample) {
    setStep({ name: 'reading' });
    try {
      const file = await sampleFile(sample);
      uncounted.current = 'sample';
      await open(file);
    } catch {
      restart(WORKER_FAILED);
    }
  }

  async function remap(current: Mapping, settings: ImportSettings) {
    const active = session.current;
    if (!active) return restart(WORKER_FAILED);
    const requestId = ++latestRequest.current;
    setStep({ ...current, settings, updating: true });
    try {
      const summary = await active.normalise(settings);
      // Ignore replies to earlier changes if the user has changed something since.
      if (requestId === latestRequest.current) {
        setStep({ ...current, settings, summary, updating: false });
      }
    } catch {
      restart(WORKER_FAILED);
    }
  }

  async function runCheck(mapping: Mapping, details: Details) {
    const active = session.current;
    if (!active) return restart(WORKER_FAILED);
    setStep({ name: 'checking' });
    try {
      const report = await active.check({ ...details, nation }, todayInLondon());
      if (!report) return restart(WORKER_FAILED);
      setStep({ name: 'report', report, mapping: { ...mapping, updating: false }, details });
      if (uncounted.current) {
        trackEvent('Rota checked', { source: uncounted.current });
        uncounted.current = null;
      }
    } catch {
      restart(WORKER_FAILED);
    }
  }

  async function addDetails(current: ReportStep) {
    const active = session.current;
    if (!active) return restart(WORKER_FAILED);
    try {
      const rota = await active.rota();
      if (rota) setStep({ name: 'details', rota, back: current });
    } catch {
      restart(WORKER_FAILED);
    }
  }

  /** Saves the rota chart PDF from the worker (T29). The file is made in this browser. */
  async function downloadChart(report: Report) {
    const pdf = await session.current?.rotaChart();
    if (!pdf) throw new Error('No rota to chart');
    const url = URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `rota-chart-${report.summary.period.start}.pdf`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  return renderStep();

  function renderStep() {
    switch (step.name) {
      case 'upload':
      case 'reading':
        return (
          <section aria-labelledby="upload-heading">
            <h2 id="upload-heading">Upload your rota</h2>
            <label class="country">
              Where is your business?
              <select
                value={nation}
                disabled={step.name === 'reading'}
                onChange={(e) => setNation(e.currentTarget.value as Nation)}
              >
                <option value="england">England</option>
                <option value="scotland">Scotland</option>
                <option value="wales">Wales</option>
                <option value="northern-ireland">Northern Ireland</option>
              </select>
            </label>
            {outOfScope && (
              <p class="callout">
                Northern Ireland has its own working time rules, which ShiftLens doesn't check. It
                covers England, Scotland and Wales.
              </p>
            )}
            <label
              class={`dropzone${dragging ? ' is-dragging' : ''}${outOfScope ? ' is-disabled' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const file = e.dataTransfer?.files[0];
                if (file && step.name !== 'reading' && !outOfScope) openFile(file);
              }}
            >
              <span class="dropzone-title">Choose your rota (CSV or Excel)</span>
              <span class="dropzone-button" aria-hidden="true">
                Choose a file
              </span>
              <span class="dropzone-hint">
                or drop the file here. Excel (.xlsx) or CSV, up to 5 MB.
              </span>
              <input
                class="visually-hidden"
                type="file"
                accept=".csv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                disabled={step.name === 'reading' || outOfScope}
                onChange={(e) => {
                  const file = e.currentTarget.files?.[0];
                  if (file) openFile(file);
                }}
              />
            </label>
            <p class="muted">
              Not sure your file will work?{' '}
              <a href="/supported-rotas">See the formats we read, or download a rota template</a>.
            </p>
            <div class="samples">
              <h3 id="samples-heading">No rota to hand? Try a sample</h3>
              <p class="muted">Made-up staff and venues, checked exactly like an upload.</p>
              <ul aria-labelledby="samples-heading">
                {SAMPLES.map((sample) => (
                  <li key={sample.id}>
                    <button
                      type="button"
                      class="link-button"
                      disabled={step.name === 'reading' || outOfScope}
                      onClick={() => void openSample(sample)}
                    >
                      {sample.label}
                    </button>{' '}
                    <span class="muted">{sample.description}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p role="status">
              {step.name === 'reading' ? 'Reading your rota…' : (step.error ?? '')}
            </p>
          </section>
        );

      case 'mapping': {
        const sheets = step.table.sheetNames ?? [];
        return (
          <>
            {sheets.length > 1 && (
              <label>
                Sheet
                <select
                  value={step.table.sheetName}
                  onChange={(e) => void open(step.file, e.currentTarget.value)}
                >
                  {sheets.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <MappingStep
              headers={step.headers}
              settings={step.settings}
              summary={step.summary}
              updating={step.updating}
              {...(step.vendorName !== undefined && { vendorName: step.vendorName })}
              onChange={(settings) => void remap(step, settings)}
              onBack={() => restart()}
              onContinue={() => void runCheck(step, NO_DETAILS)}
            />
          </>
        );
      }

      case 'details':
        return (
          <DetailsStep
            rota={step.rota}
            initial={step.back.details}
            onBack={() => setStep(step.back)}
            onDone={(details) => void runCheck(step.back.mapping, details)}
          />
        );

      case 'checking':
        return <p role="status">Checking your rota…</p>;

      case 'report':
        return (
          <ReportView
            report={step.report}
            onEditColumns={() => setStep(step.mapping)}
            onAddDetails={() => void addDetails(step)}
            onStartAgain={() => restart()}
            onDownloadChart={() => downloadChart(step.report)}
          />
        );
    }
  }
}

/** Today's date in the UK as YYYY-MM-DD, without loading Luxon on the main thread. */
function todayInLondon(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());
}
